import type { Match } from '@uefadigital/panenka';
import { PkCollapse, PkDivider } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Matches from './Matches';
import MatchResults from './MatchResults';
import StatsOverview from './StatsOverview';
import TeamsOverview from './TeamsOverview';
import { getMatchStatistics, getTeamId, splitH2HMatches } from './utils';

import { useGetMatchHeadToHeadAdvancedQuery } from 'api/fspApi';
import type { CompstatsV1TeamStatisticsHead2HeadAdvanced } from 'types/ApiResponseInterfaces';
import { UEFA_IMG_BASE_URL } from 'utils/imgUefaBaseUrl';
import { translateEntity } from 'utils/translateEntity';

interface CountryToHeadProps {
  isDesktop: boolean;
  isResultsExpanded: boolean;
  matchData: Match;
  BadgeSize: number;
  ClubName: string;
  ScoreNumber: string;
  scoreTitle: string;
  headToHeadAdvanced: CompstatsV1TeamStatisticsHead2HeadAdvanced[];
  Title: string;
}

const CountryToHead = ({
  isDesktop,
  isResultsExpanded = false,
  matchData,
  BadgeSize,
  ClubName,
  ScoreNumber,
  scoreTitle,
  headToHeadAdvanced,
  Title,
}: CountryToHeadProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(isResultsExpanded);

  const {
    data: H2HMatches = [],
    error: H2HMatchesError,
    isLoading: H2HMatchesLoading,
  } = useGetMatchHeadToHeadAdvancedQuery({
    offset: 0,
    limit: 100,
    matchId: headToHeadAdvanced[1]?.matchIds,
  });

  const homeWin = getMatchStatistics(headToHeadAdvanced, getTeamId(matchData?.awayTeam), 'matches_loss');
  const draw = getMatchStatistics(headToHeadAdvanced, getTeamId(matchData?.awayTeam), 'matches_draw');
  const awayWin = getMatchStatistics(headToHeadAdvanced, getTeamId(matchData?.awayTeam), 'matches_win');
  homeWin.title = awayWin.title;

  const numberOfPreviousMatches = Number(homeWin.value) + Number(draw.value) + Number(awayWin.value);
  const havePreviousMatches = numberOfPreviousMatches > 0;

  if (!!H2HMatchesError || H2HMatchesLoading) return null;

  const { firstHalf, secondHalf } = splitH2HMatches(H2HMatches);

  return (
    headToHeadAdvanced[1] && (
      <>
        <PkDivider class="divider--horizontal" direction="horizontal" />
        <div>
          <TeamsOverview
            homeTeam={{
              isTeamAvailable: !!matchData?.homeTeam,
              isNationalTeam: true,
              internationalName: matchData?.homeTeam?.internationalName ?? '',
              countryCode: matchData?.homeTeam?.countryCode,
              displayName: translateEntity(matchData?.homeTeam)?.countryName,
              logoUrl: `${UEFA_IMG_BASE_URL}/imgml/flags/70x70/${headToHeadAdvanced[1].againstCountryCode}.png`,
            }}
            awayTeam={{
              isTeamAvailable: !!matchData?.awayTeam,
              isNationalTeam: false,
              internationalName: matchData?.awayTeam?.internationalName ?? '',
              countryCode: matchData?.awayTeam?.countryCode,
              displayName: translateEntity(matchData?.awayTeam)?.displayName,
              logoUrl: matchData?.awayTeam?.logoUrl,
            }}
            isDesktop={isDesktop}
            badgeSize={BadgeSize}
            clubName={ClubName}
          />
          <StatsOverview
            homeWin={homeWin}
            draw={draw}
            awayWin={awayWin}
            isDesktop={isDesktop}
            scoreNumber={ScoreNumber}
            scoreTitle={scoreTitle}
            havePreviousMatches={havePreviousMatches}
          />
        </div>
        <div className="pk-mt--xs pk-mt-md--m pr-display--none">
          {isDesktop && havePreviousMatches && H2HMatches.length ? (
            <div className="pk-w--100 pk-d--flex">
              <PkCollapse
                className="pk-ml--auto"
                expandedLabel={t('see_matches')}
                collapsedLabel={t('see_matches')}
                expand={isExpanded}
                onPkChange={() => setIsExpanded(!isExpanded)}
              />
            </div>
          ) : (
            <div />
          )}
        </div>
        {isExpanded ? (
          <div>
            {isDesktop ? (
              <Matches title={Title} firstHalf={firstHalf} secondHalf={secondHalf} />
            ) : (
              <div className="pk-px--s pk-px-md--xl2">
                <MatchResults H2HMatchesToDisplay={H2HMatches} />
              </div>
            )}
          </div>
        ) : (
          ''
        )}
        <div
          className={clsx({
            'pk-mt--m pk-mt-md--l': havePreviousMatches,
          })}
        >
          {!isDesktop && havePreviousMatches && H2HMatches.length ? (
            <div className="pk-w--100 pk-d--flex">
              <PkCollapse
                className="pk-ml--auto"
                expandedLabel={t('see_matches')}
                collapsedLabel={t('see_matches')}
                expand={isExpanded}
                onPkChange={() => setIsExpanded(!isExpanded)}
              />
            </div>
          ) : (
            <div />
          )}
        </div>
      </>
    )
  );
};

export default CountryToHead;
